import { motion } from "framer-motion";
import { Check, MessageCircle } from "lucide-react";
import { useWhatsAppNumber, buildWhatsAppLink } from "@/lib/api";

const base = [
  "Anamnesi corporea completa",
  "Scheda di allenamento 100% personalizzata",
  "Piano di consigli alimentari su misura",
  "File Excel interattivo (fabbisogno e sostituzione pasti)",
  "Assistenza diretta su WhatsApp",
];

const packages = [
  {
    id: "mensile",
    name: "Mensile",
    duration: "1 mese",
    price: 99,
    badge: "Partenza",
    popular: false,
    features: base,
  },
  {
    id: "trimestrale",
    name: "Trimestrale",
    duration: "3 mesi",
    price: 199,
    badge: "Consigliato",
    popular: true,
    features: [...base, "Schede rinnovate gratuitamente ogni 45 giorni"],
  },
  {
    id: "semestrale",
    name: "Semestrale",
    duration: "6 mesi",
    price: 349,
    badge: null,
    popular: false,
    features: [...base, "Schede rinnovate gratuitamente ogni 45 giorni"],
  },
  {
    id: "annuale",
    name: "Annuale",
    duration: "12 mesi",
    price: 599,
    badge: "Max impegno",
    popular: false,
    note: "≈ 50€ al mese",
    features: [...base, "Schede rinnovate gratuitamente ogni 45 giorni", "12 mesi di coaching fianco a fianco"],
  },
];

export default function PricingSection() {
  const whatsapp = useWhatsAppNumber();

  return (
    <section id="pacchetti" className="py-24 px-5 sm:px-8">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.7 }}
          className="mb-14 text-center max-w-2xl mx-auto"
        >
          <p className="font-mono text-[11px] uppercase tracking-[0.35em] text-blood font-semibold mb-4">// I pacchetti</p>
          <h2 className="font-display font-black uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">
            Scegli la tua <span className="text-blood">disciplina</span>
          </h2>
          <p className="mt-4 text-neutral-300 text-base sm:text-lg">
            Scegli il pacchetto e scrivimi su WhatsApp: partiamo subito. Il file Excel interattivo è
            incluso in ogni pacchetto.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-5 items-stretch">
          {packages.map((pkg, i) => (
            <motion.div
              key={pkg.id}
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.6, delay: i * 0.1 }}
              className={`relative flex flex-col p-7 transition-colors duration-300 ${
                pkg.popular
                  ? "bg-ink-card border-2 border-blood red-glow xl:-translate-y-3"
                  : "bg-ink-surface border border-white/10 hover:border-blood/40"
              }`}
              data-testid={`pricing-card-${pkg.id}`}
            >
              {pkg.badge && (
                <span
                  className={`absolute -top-3 left-1/2 -translate-x-1/2 font-mono text-[9px] uppercase tracking-[0.25em] font-semibold px-3 py-1 whitespace-nowrap ${
                    pkg.popular ? "bg-blood text-white" : "bg-ink-elev text-neutral-300 border border-white/15"
                  }`}
                >
                  {pkg.badge}
                </span>
              )}

              <h3 className="font-display font-extrabold uppercase text-3xl tracking-tight">{pkg.name}</h3>
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-400 mt-1">{pkg.duration}</p>

              <div className="mt-5 flex items-end gap-2">
                <span className="font-display font-black text-6xl leading-none">€{pkg.price}</span>
              </div>
              {pkg.note && <p className="font-mono text-[10px] uppercase tracking-wider text-blood mt-2">{pkg.note}</p>}

              <ul className="mt-6 space-y-3 flex-1">
                {pkg.features.map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-sm text-neutral-200 leading-snug">
                    <Check size={15} className="text-blood mt-0.5 shrink-0" strokeWidth={3} />
                    {f}
                  </li>
                ))}
              </ul>

              <motion.a
                data-testid={`whatsapp-buy-btn-${pkg.id}`}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                href={buildWhatsAppLink(whatsapp, `Ciao! Voglio iniziare con il pacchetto ${pkg.name} da ${pkg.price}€ di DisciplinaFit.`)}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-7 w-full bg-blood hover:bg-blood-hot text-white font-display font-bold uppercase tracking-wider text-lg py-3 transition-colors flex items-center justify-center gap-2"
              >
                Acquista
              </motion.a>
              <a
                data-testid={`whatsapp-info-btn-${pkg.id}`}
                href={buildWhatsAppLink(whatsapp, `Ciao! Vorrei più informazioni sul pacchetto ${pkg.name} di DisciplinaFit.`)}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-3 w-full border-t border-white/10 pt-3 flex items-center justify-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-400 hover:text-wa transition-colors"
              >
                <MessageCircle size={13} className="text-wa" /> Più info su WhatsApp
              </a>
            </motion.div>
          ))}
        </div>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mt-10 text-center font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500"
        >
          Ordine e pagamento concordati direttamente su WhatsApp · Risposta rapida
        </motion.p>
      </div>
    </section>
  );
}
